import React, { useState, useRef } from "react";
import {
  Settings,
  Palette,
  Layout,
  Tag,
  Building2,
  HardDrive,
  Shield,
  Info,
  Upload,
  Download,
  Trash2,
  Check,
  Database,
  X,
  Layers,
  Users,
  TrendingUp,
  Lock,
  Wallet,
  Briefcase,
  Clock,
  Mail,
  Award,
} from "lucide-react";
import { HELP } from "../constants/helpContent";
import { PersonaSettings } from "../components/settings/PersonaSettings";
import { SchemaViewer } from "../components/settings/SchemaViewer";
import { CustomFieldAdder } from "../components/settings/CustomFieldAdder";
import { UsersPanel } from "../components/settings/UsersPanel";
import { ScoringPanel, RecalculatePanel } from "../components/settings/ScoringPanel";
import { CapacityWeightsPanel } from "../components/settings/CapacityWeightsPanel";
import { WealthImportPanel } from "../components/settings/WealthImportPanel";
import { ChapterSettingsPanel } from "../components/settings/ChapterSettingsPanel";
import { GivingLevelsPanel } from "../components/settings/GivingLevelsPanel";
import { EmailTemplatesPanel } from "../components/settings/EmailTemplatesPanel";
import { DirectorsPanel } from "../components/settings/DirectorsPanel";
import { FundsPanel } from "../components/settings/FundsPanel";
import { PermissionsPanel } from "../components/settings/PermissionsPanel";
import { useApp } from "../context/AppContext";
import { PALETTES } from "../constants/palettes";
import { TOOLBAR_STYLES, APP_NAME, APP_VERSION, STORAGE_KEY, BACKUP_PREFIX, FONT_SIZES, FONT_FAMILY, ICON_SIZES, ICON_SCALE_OPTIONS } from "../constants/appConfig";
import { debugLog } from "../utils/debugLog";
import { eventService } from "../services/eventService";
import { ViewLayout } from "../components/ui/ViewLayout";
import { Card } from "../components/ui/Card";
import { ActionButton } from "../components/ui/ActionButton";
import { TabBar } from "../components/ui/TabBar";
import { ConfirmDialog } from "../components/ui/Modal";
import {
  ZoneOneHeader,
  ZoneThreeFooter,
} from "../components/zones/ZoneHeaderFooter";
import { ZoneTwoToolbar } from "../components/zones/ZoneTwoToolbar";
import { PathwayAdder } from "../components/features/PathwayAdder";
import {
  getTemplate,
  buildDefaultPermissions,
} from "../constants/permissionDefaults";

const SETTINGS_TABS = [
  { id: "appearance", label: "Appearance", icon: Palette },
  { id: "fields", label: "Fields", icon: Layers },
  { id: "scoring", label: "Scoring", icon: TrendingUp },
  { id: "chapter", label: "Organization", icon: Building2 },
  { id: "givingLevels", label: "Giving Levels", icon: Award },
  { id: "emailTemplates", label: "Email Templates", icon: Mail },
  { id: "directors", label: "Directors", icon: Briefcase },
  { id: "funds", label: "Funds", icon: Wallet },
  { id: "data", label: "Data", icon: HardDrive },
  { id: "users", label: "Users", icon: Users },
  { id: "permissions", label: "Permissions", icon: Lock },
  { id: "schema", label: "Schema", icon: Database },
  { id: "about", label: "About", icon: Info },
  { id: "personas", label: "Personas", icon: Users },
];

// ── Icon Scale Card (Appearance tab) ─────────────────────────
//
// Presets change the global ICON_SIZES proxy scale; every icon
// throughout the app rescales on the next render.
const IconScaleCard = ({ palette }) => {
  const { iconScale, setIconScale } = useApp();
  return (
    <Card style={{ marginBottom: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 6,
        }}
      >
        <Layers size={ICON_SIZES.lg} style={{ color: palette.accent1 }} />
        <div
          style={{
            fontSize: FONT_SIZES.base,
            fontWeight: 700,
            color: palette.text1,
            fontFamily: FONT_FAMILY,
          }}
        >
          Icon Size
        </div>
      </div>
      <p
        style={{
          color: palette.text2,
          fontSize: FONT_SIZES.sm,
          margin: "0 0 14px 0",
          lineHeight: 1.5,
        }}
      >
        Scale every icon in the app at once. Saved to this browser —
        change freely without affecting other team members.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10,
        }}
      >
        {ICON_SCALE_OPTIONS.map((opt) => {
          const active = iconScale === opt.id;
          const previewSize = Math.round((18 * opt.id) / 100);
          return (
            <button
              key={opt.id}
              onClick={() => setIconScale(opt.id)}
              style={{
                padding: "14px 10px",
                borderRadius: 10,
                cursor: "pointer",
                border: active
                  ? `2px solid ${palette.accent1}`
                  : `2px solid ${palette.cardBorder}`,
                background: active ? `${palette.accent1}0a` : "transparent",
                color: active ? palette.accent1 : palette.text1,
                fontFamily: FONT_FAMILY,
                fontWeight: 600,
                fontSize: FONT_SIZES.sm,
                transition: "all 0.15s",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 6,
              }}
            >
              <div
                style={{
                  width: previewSize,
                  height: previewSize,
                  borderRadius: previewSize / 4,
                  background: active ? palette.accent1 : palette.text3,
                }}
              />
              <span>{opt.label}</span>
              <span
                style={{
                  fontSize: 10,
                  color: palette.text3,
                  fontWeight: 500,
                }}
              >
                {opt.id}%
              </span>
            </button>
          );
        })}
      </div>
    </Card>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN SETTINGS VIEW
// ═══════════════════════════════════════════════════════════════

export const SettingsView = () => {
  const {
    palette,
    paletteId,
    setPaletteId,
    goBack,
    goHome,
    members,
    setMembers,
    importMembers,
    showMessage,
    chapterName,
    setChapterName,
    chapterSettings,
    setChapterSettings,
    toolbarStyle,
    setToolbarStyle,
    sessionTimeout,
    setSessionTimeout,
    pathways,
    setPathways,
    addPathway,
    removePathway,
    customFields,
    setCustomFields,
    isAdmin,
    rolePermissions,
    setRolePermissions,
  } = useApp();
  const fileRef = useRef(null);
  const [showClear, setShowClear] = useState(false);
  const [activeTab, setActiveTab] = useState("appearance");

  const visibleTabs = isAdmin
    ? SETTINGS_TABS
    : SETTINGS_TABS.filter((t) => t.id !== "users" && t.id !== "permissions");

  // ── Backup with events + chapter settings ──────────────────
  const handleBackup = async () => {
    let eventsData = { events: [], attendance: [] };
    try {
      eventsData = await eventService.exportAll();
    } catch (err) {
      debugLog.error("Backup: failed to export events", err);
    }

    const data = {
      appName: APP_NAME,
      version: APP_VERSION,
      exportDate: new Date().toISOString(),
      source: "supabase",
      memberCount: members.length,
      members,
      settings: {
        paletteId,
        chapterName,
        chapterSettings,
        toolbarStyle,
        pathways,
        customFields,
        rolePermissions,
      },
      events: eventsData.events,
      eventAttendance: eventsData.attendance,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${BACKUP_PREFIX}_${new Date()
      .toISOString()
      .slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    showMessage("Backup downloaded");
  };

  // ── Restore with events + chapter settings ─────────────────
  const [isRestoring, setIsRestoring] = useState(false);
  const handleRestore = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!data.members || !Array.isArray(data.members)) {
          showMessage("Invalid backup — missing donors array", "error");
          return;
        }
        setIsRestoring(true);
        showMessage(`Restoring ${data.members.length} donors...`);
        await importMembers(data.members);
        if (data.settings?.paletteId) setPaletteId(data.settings.paletteId);
        if (data.settings?.chapterName)
          setChapterName(data.settings.chapterName);
        if (data.settings?.chapterSettings) {
          setChapterSettings((prev) => ({
            ...prev,
            ...data.settings.chapterSettings,
          }));
        }
        if (data.settings?.toolbarStyle)
          setToolbarStyle(data.settings.toolbarStyle);
        if (
          data.settings?.customFields &&
          Array.isArray(data.settings.customFields)
        )
          setCustomFields(data.settings.customFields);
        if (data.settings?.pathways && Array.isArray(data.settings.pathways)) {
          for (const pw of data.settings.pathways) {
            if (!pathways.find((p) => p.id === pw.id)) await addPathway(pw);
          }
        }
        if (data.settings?.rolePermissions) {
          const defaults = buildDefaultPermissions();
          const merged = { ...defaults };
          Object.keys(data.settings.rolePermissions).forEach((roleId) => {
            const template = getTemplate(roleId);
            if (template && !template.locked) {
              const stored = data.settings.rolePermissions[roleId];
              merged[roleId] = {
                template: stored?.template || roleId,
                customized: stored?.customized || false,
                permissions: {
                  ...template.permissions,
                  ...(stored?.permissions || {}),
                },
              };
            }
          });
          setRolePermissions(merged);
        }

        // Restore events if present in backup
        if (data.events && data.events.length > 0) {
          try {
            const eventResult = await eventService.importAll({
              events: data.events,
              attendance: data.eventAttendance || [],
            });
            debugLog.data("Events restored", eventResult);
          } catch (err) {
            debugLog.error("Restore: events import failed", err);
            showMessage(
              "Donors restored, but events failed to restore",
              "warning"
            );
          }
        }

        showMessage(`Restored ${data.members.length} donors to database`);
      } catch (error) {
        showMessage("Restore error — " + error.message, "error");
      }
      setIsRestoring(false);
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const SectionHeader = ({ icon: SI, title }) => (
    <h3
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        margin: "0 0 16px",
        fontSize: FONT_SIZES.md,
        fontWeight: 600,
        color: palette.text1,
        paddingBottom: 12,
        borderBottom: `1px solid ${palette.cardBorder}`,
      }}
    >
      <SI size={ICON_SIZES.md} style={{ color: palette.accent1 }} /> {title}
    </h3>
  );

  return (
    <ViewLayout>
      <ZoneOneHeader
        title="Settings"
        subtitle="Themes, branding & data management"
        icon={Settings}
        helpContent={
          activeTab === "chapter"
            ? HELP.organization
            : activeTab === "permissions"
            ? HELP.permissions || HELP.settings
            : HELP.settings
        }
        onBack={goBack}
        onHome={goHome}
      />
      <ZoneTwoToolbar />
      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TabBar
          tabs={visibleTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />
        <div style={{ flex: 1, padding: 24, overflowY: "auto" }}>
          <div style={{ maxWidth: 700, margin: "0 auto" }}>
            {activeTab === "appearance" && (
              <>
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader icon={Palette} title="Color Theme" />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(130px, 1fr))",
                      gap: 12,
                    }}
                  >
                    {Object.values(PALETTES).map((p) => (
                      <div
                        key={p.id}
                        onClick={() => setPaletteId(p.id)}
                        style={{
                          padding: 12,
                          borderRadius: 10,
                          cursor: "pointer",
                          border:
                            paletteId === p.id
                              ? `2px solid ${p.accent1}`
                              : "2px solid transparent",
                          background:
                            paletteId === p.id
                              ? `${p.accent1}06`
                              : "transparent",
                          transition: "all 0.2s",
                        }}
                      >
                        <div
                          style={{
                            height: 32,
                            borderRadius: 6,
                            marginBottom: 8,
                            background: p.preview,
                          }}
                        />
                        <div
                          style={{
                            fontSize: FONT_SIZES.sm,
                            fontWeight: 600,
                            textAlign: "center",
                            color:
                              paletteId === p.id ? p.accent1 : palette.text1,
                          }}
                        >
                          {p.name}
                        </div>
                        {paletteId === p.id && (
                          <div style={{ textAlign: "center", marginTop: 4 }}>
                            <Check size={ICON_SIZES.sm} style={{ color: p.accent1 }} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
                <Card>
                  <SectionHeader icon={Layout} title="Toolbar Style" />
                  <p
                    style={{
                      color: palette.text2,
                      fontSize: FONT_SIZES.sm,
                      marginBottom: 12,
                    }}
                  >
                    Choose how navigation buttons appear in the command toolbar.
                  </p>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: 12,
                    }}
                  >
                    {TOOLBAR_STYLES.map((ts) => (
                      <div
                        key={ts.id}
                        onClick={() => setToolbarStyle(ts.id)}
                        style={{
                          padding: 14,
                          borderRadius: 10,
                          cursor: "pointer",
                          textAlign: "center",
                          border:
                            toolbarStyle === ts.id
                              ? `2px solid ${palette.accent1}`
                              : `2px solid ${palette.cardBorder}`,
                          background:
                            toolbarStyle === ts.id
                              ? `${palette.accent1}08`
                              : "transparent",
                          transition: "all 0.2s",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 6,
                            marginBottom: 10,
                          }}
                        >
                          {ts.id === "icon-dot" &&
                            [palette.accent1, "#8b5cf6", "#3b82f6"].map(
                              (c, i) => (
                                <div
                                  key={i}
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 2,
                                  }}
                                >
                                  <div
                                    style={{
                                      position: "relative",
                                      width: 18,
                                      height: 18,
                                      borderRadius: 4,
                                      background: `${c}20`,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: 2,
                                        background: c,
                                      }}
                                    />
                                    <div
                                      style={{
                                        position: "absolute",
                                        top: -2,
                                        right: -3,
                                        width: 6,
                                        height: 6,
                                        borderRadius: "50%",
                                        background: c,
                                        border: "1.5px solid white",
                                      }}
                                    />
                                  </div>
                                  <div
                                    style={{
                                      width: 16,
                                      height: 3,
                                      borderRadius: 1,
                                      background: `${c}40`,
                                    }}
                                  />
                                </div>
                              )
                            )}
                          {ts.id === "solid" &&
                            [
                              palette.accent1,
                              palette.accent1,
                              palette.accent1,
                            ].map((c, i) => (
                              <div
                                key={i}
                                style={{
                                  width: 32,
                                  height: 18,
                                  borderRadius: 4,
                                  background: c,
                                  opacity: 0.7 + i * 0.1,
                                }}
                              />
                            ))}
                          {ts.id === "pill" &&
                            ["#6366f1", "#8b5cf6", "#3b82f6"].map((c, i) => (
                              <div
                                key={i}
                                style={{
                                  width: 32,
                                  height: 18,
                                  borderRadius: 10,
                                  border: `2px solid ${c}`,
                                  background: `${c}15`,
                                }}
                              />
                            ))}
                        </div>
                        <div
                          style={{
                            fontSize: FONT_SIZES.sm,
                            fontWeight: 600,
                            color:
                              toolbarStyle === ts.id
                                ? palette.accent1
                                : palette.text1,
                          }}
                        >
                          {ts.name}
                        </div>
                        <div
                          style={{
                            fontSize: FONT_SIZES.xs,
                            color: palette.text3,
                            marginTop: 2,
                          }}
                        >
                          {ts.desc}
                        </div>
                        {toolbarStyle === ts.id && (
                          <div style={{ marginTop: 6 }}>
                            <Check
                              size={ICON_SIZES.sm}
                              style={{ color: palette.accent1 }}
                            />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Card>
                <Card style={{ marginTop: 16 }}>
                  <SectionHeader icon={Tag} title="Font Size" />
                  <p
                    style={{
                      color: palette.text2,
                      fontSize: FONT_SIZES.sm,
                      marginBottom: 16,
                    }}
                  >
                    Adjust the text size across the entire application.
                  </p>
                  <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                    <span style={{ fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY, whiteSpace: "nowrap" }}>
                      Aa
                    </span>
                    <input
                      type="range"
                      min="80"
                      max="140"
                      step="5"
                      value={chapterSettings.fontScale || 100}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        setChapterSettings((prev) => ({ ...prev, fontScale: val }));
                      }}
                      style={{ flex: 1, cursor: "pointer" }}
                    />
                    <span style={{ fontSize: FONT_SIZES.lg, color: palette.text3, fontFamily: FONT_FAMILY, whiteSpace: "nowrap" }}>
                      Aa
                    </span>
                    <span style={{
                      fontSize: FONT_SIZES.sm, fontWeight: 600,
                      color: palette.text1, fontFamily: FONT_FAMILY,
                      minWidth: 45, textAlign: "center",
                    }}>
                      {chapterSettings.fontScale || 100}%
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
                    {[
                      { label: "Compact", value: 85 },
                      { label: "Default", value: 100 },
                      { label: "Large", value: 115 },
                      { label: "Extra Large", value: 130 },
                    ].map((preset) => (
                      <button
                        key={preset.value}
                        onClick={() =>
                          setChapterSettings((prev) => ({ ...prev, fontScale: preset.value }))
                        }
                        style={{
                          padding: "4px 12px",
                          borderRadius: 6,
                          fontSize: FONT_SIZES.xs,
                          fontWeight: 600,
                          fontFamily: FONT_FAMILY,
                          cursor: "pointer",
                          border: "none",
                          background:
                            (chapterSettings.fontScale || 100) === preset.value
                              ? palette.accent1
                              : `${palette.accent1}10`,
                          color:
                            (chapterSettings.fontScale || 100) === preset.value
                              ? "#fff"
                              : palette.accent1,
                          transition: "all 0.15s",
                        }}
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                  <div style={{
                    marginTop: 16, padding: "12px 16px",
                    background: `${palette.accent1}06`,
                    borderRadius: 8,
                    fontSize: FONT_SIZES.sm,
                    color: palette.text1,
                    fontFamily: FONT_FAMILY,
                    lineHeight: 1.5,
                  }}>
                    Preview: This is how body text will appear at {chapterSettings.fontScale || 100}% scale.
                    Labels, values, and all interface elements adjust proportionally.
                  </div>
                </Card>
                <Card style={{ marginTop: 16 }}>
                  <SectionHeader icon={Clock} title="Session Timeout" />
                  <p
                    style={{
                      color: palette.text2,
                      fontSize: FONT_SIZES.sm,
                      marginBottom: 16,
                    }}
                  >
                    Automatically log out after a period of inactivity. Activity
                    includes mouse clicks, key presses, scrolling, and touch events.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {[
                      { label: "15 min", value: 15 },
                      { label: "30 min", value: 30 },
                      { label: "1 hour", value: 60 },
                      { label: "2 hours", value: 120 },
                      { label: "4 hours", value: 240 },
                      { label: "Never", value: 0 },
                    ].map((option) => (
                      <button
                        key={option.value}
                        onClick={() => setSessionTimeout(option.value)}
                        style={{
                          padding: "6px 16px",
                          borderRadius: 8,
                          fontSize: FONT_SIZES.sm,
                          fontWeight: 600,
                          fontFamily: FONT_FAMILY,
                          cursor: "pointer",
                          border: "none",
                          background:
                            sessionTimeout === option.value
                              ? palette.accent1
                              : `${palette.accent1}10`,
                          color:
                            sessionTimeout === option.value
                              ? "#fff"
                              : palette.accent1,
                          transition: "all 0.15s",
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {sessionTimeout > 0 && (
                    <div
                      style={{
                        marginTop: 16,
                        padding: "10px 14px",
                        borderRadius: 8,
                        background: `${palette.accent1}06`,
                        border: `1px solid ${palette.accent1}15`,
                        fontSize: FONT_SIZES.xs,
                        color: palette.text2,
                        textAlign: "center",
                      }}
                    >
                      <Clock
                        size={ICON_SIZES.sm}
                        style={{ verticalAlign: "middle", marginRight: 6 }}
                      />
                      You will be logged out after{" "}
                      <strong style={{ color: palette.text1 }}>
                        {sessionTimeout >= 60
                          ? `${sessionTimeout / 60} hour${sessionTimeout > 60 ? "s" : ""}`
                          : `${sessionTimeout} minutes`}
                      </strong>{" "}
                      of inactivity.
                    </div>
                  )}
                </Card>
                <IconScaleCard palette={palette} />
              </>
            )}

            {activeTab === "fields" && (
              <>
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader icon={Tag} title="Donor Pathways" />
                  <p
                    style={{
                      color: palette.text2,
                      fontSize: FONT_SIZES.sm,
                      marginBottom: 16,
                    }}
                  >
                    Define the engagement pathways for your organization. New donors
                    default to the first pathway.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      marginBottom: 16,
                    }}
                  >
                    {pathways.map((pw) => {
                      const usageCount = members.filter(
                        (m) => m.pathway === pw.id
                      ).length;
                      return (
                        <div
                          key={pw.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "10px 14px",
                            borderRadius: 8,
                            border: `1px solid ${palette.cardBorder}`,
                            background: palette.inputBg,
                          }}
                        >
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: 6,
                              background: pw.color,
                              flexShrink: 0,
                            }}
                          />
                          <span
                            style={{
                              flex: 1,
                              fontSize: FONT_SIZES.sm,
                              fontWeight: 600,
                              color: palette.text1,
                            }}
                          >
                            {pw.name}
                          </span>
                          <span
                            style={{
                              fontSize: FONT_SIZES.xs,
                              color: palette.text3,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {usageCount} donor{usageCount !== 1 ? "s" : ""}
                          </span>
                          {pathways.length > 1 ? (
                            usageCount > 0 ? (
                              <span
                                title={`Cannot delete — ${usageCount} donor(s) assigned`}
                                style={{
                                  color: palette.text3,
                                  cursor: "not-allowed",
                                  display: "flex",
                                }}
                              >
                                <X size={ICON_SIZES.sm} />
                              </span>
                            ) : (
                              <button
                                onClick={() => removePathway(pw.id)}
                                title="Remove pathway"
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  color: "#ef4444",
                                  padding: 2,
                                  display: "flex",
                                }}
                              >
                                <X size={ICON_SIZES.sm} />
                              </button>
                            )
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                  <PathwayAdder />
                </Card>
                <Card>
                  <SectionHeader icon={Layers} title="Custom Fields" />
                  <p
                    style={{
                      color: palette.text2,
                      fontSize: FONT_SIZES.sm,
                      marginBottom: 16,
                    }}
                  >
                    Define custom fields that appear on every donor's detail
                    view. Values are stored per donor.
                  </p>
                  {customFields.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        marginBottom: 16,
                      }}
                    >
                      {customFields.map((cf) => (
                        <div
                          key={cf.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "10px 14px",
                            borderRadius: 8,
                            border: `1px solid ${palette.cardBorder}`,
                            background: palette.inputBg,
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <span
                              style={{
                                fontSize: FONT_SIZES.sm,
                                fontWeight: 600,
                                color: palette.text1,
                              }}
                            >
                              {cf.label}
                            </span>
                            <span
                              style={{
                                fontSize: FONT_SIZES.xs,
                                color: palette.text3,
                                marginLeft: 8,
                              }}
                            >
                              {cf.type}
                              {cf.type === "dropdown"
                                ? ` (${(cf.options || []).length} options)`
                                : ""}
                            </span>
                          </div>
                          <button
                            onClick={() => {
                              setCustomFields((prev) =>
                                prev.filter((f) => f.id !== cf.id)
                              );
                              showMessage(`"${cf.label}" field removed`);
                            }}
                            title="Remove field"
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: "#ef4444",
                              padding: 2,
                              display: "flex",
                            }}
                          >
                            <X size={ICON_SIZES.sm} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <CustomFieldAdder
                    customFields={customFields}
                    setCustomFields={setCustomFields}
                    showMessage={showMessage}
                    palette={palette}
                  />
                </Card>
              </>
            )}

            {/* ── Scoring tab: config + recalculate ── */}
            {activeTab === "scoring" && (
              <>
                <ScoringPanel palette={palette} />
                <CapacityWeightsPanel palette={palette} />
                <RecalculatePanel />
              </>
            )}

            {/* ── Organization tab: full receipt configuration ── */}
            {activeTab === "chapter" && (
              <ChapterSettingsPanel palette={palette} />
            )}

            {activeTab === "givingLevels" && (
              <GivingLevelsPanel palette={palette} />
            )}

            {activeTab === "emailTemplates" && (
              <EmailTemplatesPanel palette={palette} />
            )}

            {activeTab === "personas" && <PersonaSettings />}

            {activeTab === "directors" && <DirectorsPanel palette={palette} />}

            {activeTab === "funds" && <FundsPanel palette={palette} />}

            {activeTab === "data" && (
              <>
                <WealthImportPanel palette={palette} />
                <Card style={{ marginBottom: 16 }}>
                  <SectionHeader icon={HardDrive} title="Backup & Restore" />
                  <p
                    style={{
                      color: palette.text2,
                      fontSize: FONT_SIZES.sm,
                      marginBottom: 16,
                    }}
                  >
                    JSON format, DB-migration ready. Includes donors, settings,
                    organization configuration, events, and attendance.
                  </p>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <ActionButton
                      label="Download Backup"
                      icon={Download}
                      onClick={handleBackup}
                    />
                    <input
                      ref={fileRef}
                      type="file"
                      accept=".json"
                      onChange={handleRestore}
                      style={{ display: "none" }}
                    />
                    <ActionButton
                      label={isRestoring ? "Restoring..." : "Restore"}
                      icon={Upload}
                      variant="secondary"
                      onClick={() => fileRef.current?.click()}
                    />
                  </div>
                  <div
                    style={{
                      marginTop: 16,
                      padding: "10px 14px",
                      borderRadius: 8,
                      background: `${palette.accent1}06`,
                      border: `1px solid ${palette.accent1}15`,
                      fontSize: FONT_SIZES.xs,
                      color: palette.text2,
                    }}
                  >
                    <Database
                      size={ICON_SIZES.sm}
                      style={{ verticalAlign: "middle", marginRight: 6 }}
                    />
                    <strong>Connected:</strong> Data syncs to Supabase
                    PostgreSQL. Backups export current database state.
                  </div>
                </Card>
                <Card>
                  <SectionHeader icon={Shield} title="Data Management" />
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          margin: "0 0 4px",
                          fontSize: FONT_SIZES.sm,
                          color: palette.text1,
                          fontWeight: 500,
                        }}
                      >
                        {members.length} donors stored
                      </p>
                      <p
                        style={{
                          margin: 0,
                          fontSize: FONT_SIZES.xs,
                          color: palette.text2,
                        }}
                      >
                        localStorage •{" "}
                        {(JSON.stringify(members).length / 1024).toFixed(1)} KB
                      </p>
                    </div>
                    <ActionButton
                      label="Clear All"
                      icon={Trash2}
                      variant="danger"
                      small
                      onClick={() => setShowClear(true)}
                    />
                  </div>
                </Card>
              </>
            )}

            {activeTab === "users" && <UsersPanel palette={palette} />}

            {activeTab === "permissions" && (
              <PermissionsPanel
                palette={palette}
                rolePermissions={rolePermissions}
                setRolePermissions={setRolePermissions}
              />
            )}

            {activeTab === "schema" && <SchemaViewer />}

            {activeTab === "about" && (
              <>
                <Card>
                  <SectionHeader icon={Info} title="About" />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 10,
                      fontSize: FONT_SIZES.sm,
                    }}
                  >
                    {[
                      ["Application", APP_NAME],
                      ["Version", `v${APP_VERSION}`],
                      ["Architecture", "Card-Centric / Supabase"],
                      ["Phase", "2 — Board Role Separation"],
                      ["Backend", "Supabase PostgreSQL"],
                      ["Platform", "React"],
                    ].map(([label, value]) => (
                      <div key={label}>
                        <span style={{ color: palette.text2 }}>{label}:</span>{" "}
                        <span style={{ color: palette.text1, fontWeight: 500 }}>
                          {value}
                        </span>
                      </div>
                    ))}
                  </div>
                </Card>
                <Card style={{ marginTop: 16 }}>
                  <div style={{ textAlign: "center", padding: "20px 16px" }}>
                    <div
                      style={{
                        fontSize: "1.3rem",
                        fontWeight: 800,
                        color: palette.text1,
                        fontFamily: FONT_FAMILY,
                        letterSpacing: "0.02em",
                        marginBottom: 8,
                      }}
                    >
                      ClearView Development Platform
                    </div>
                    <div
                      style={{
                        fontSize: FONT_SIZES.md,
                        fontWeight: 600,
                        color: palette.accent1,
                        marginBottom: 12,
                      }}
                    >
                      © {new Date().getFullYear()} ClearView Development
                      Platform
                    </div>
                    <div
                      style={{
                        fontSize: FONT_SIZES.sm,
                        color: palette.text2,
                        lineHeight: 1.6,
                        maxWidth: 480,
                        margin: "0 auto",
                      }}
                    >
                      All rights reserved. This software and its associated
                      documentation are proprietary to ClearView Development
                      Platform. Unauthorized reproduction, distribution, or
                      modification of this application or any portion thereof is
                      strictly prohibited.
                    </div>
                    <div
                      style={{
                        fontSize: FONT_SIZES.xs,
                        color: palette.text3,
                        marginTop: 16,
                        paddingTop: 12,
                        borderTop: `1px solid ${palette.cardBorder}`,
                      }}
                    >
                      Designed and developed by Glen Quiring
                    </div>
                  </div>
                </Card>
              </>
            )}
          </div>
        </div>
      </div>
      <ZoneThreeFooter />
      {showClear && (
        <ConfirmDialog
          title="Clear All Data?"
          message={
            <>
              Delete all {members.length} donors?
              <br />
              <strong>Download a backup first.</strong>
            </>
          }
          confirmLabel="Clear All"
          variant="danger"
          onConfirm={() => {
            setMembers([]);
            localStorage.removeItem(STORAGE_KEY);
            showMessage("Cleared", "warning");
            setShowClear(false);
          }}
          onCancel={() => setShowClear(false)}
        />
      )}
    </ViewLayout>
  );
};
